/**
 * Admin - Check-In/Check-Out Agent Analytics & Performance
 * Detailed analytics and performance metrics for a specific check-in/check-out agent
 */

import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Grid, Card, CircularProgress, Chip, Avatar, Divider, IconButton } from "@mui/material";
import Icon from "@mui/material/Icon";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";

// API
import { adminAPI } from "utils/api";

function AgentAnalytics() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [analytics, setAnalytics] = useState({
    totalOperations: 0,
    totalCheckIns: 0,
    totalCheckOuts: 0,
    activeBookings: 0,
    completedOperations: 0,
    averageOperationTime: 0,
    operationsByMonth: [],
    operationsByType: [],
    peakHours: [],
    operationsByDayOfWeek: [],
    customerSatisfaction: 0,
    onTimeRate: 0,
  });

  useEffect(() => {
    if (agentId) {
      fetchAgentAnalytics();
    }
  }, [agentId]);

  const fetchAgentAnalytics = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCheckInOutAgentAnalytics(agentId);

      setAgent(response.agent);
      setAnalytics(response.analytics || analytics);
    } catch (error) {
      console.error("Error fetching agent analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getStatusColor = (isActive) => {
    return isActive !== false ? "success" : "error";
  };

  const getStatusLabel = (isActive) => {
    return isActive !== false ? "Active" : "Inactive";
  };

  // Prepare chart data
  const operationsOverTimeChart = {
    labels: analytics.operationsByMonth.map((item) => item.month) || [],
    datasets: [
      {
        label: "Check-Ins",
        tension: 0.4,
        borderWidth: 0,
        pointRadius: 0,
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        fill: true,
        data: analytics.operationsByMonth.map((item) => item.checkIns) || [],
      },
      {
        label: "Check-Outs",
        tension: 0.4,
        borderWidth: 0,
        pointRadius: 0,
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.1)",
        fill: true,
        data: analytics.operationsByMonth.map((item) => item.checkOuts) || [],
      },
    ],
  };

  const operationsByTypeChart = {
    labels: analytics.operationsByType.map((item) => item.type) || [],
    datasets: {
      label: "Operations",
      data: analytics.operationsByType.map((item) => item.count) || [],
      backgroundColors: ["info", "success"],
    },
  };

  const peakHoursChart = {
    labels: analytics.peakHours.map((item) => item.hour) || [],
    datasets: {
      label: "Operations",
      data: analytics.peakHours.map((item) => item.count) || [],
    },
  };

  const dayOfWeekChart = {
    labels: analytics.operationsByDayOfWeek.map((item) => item.day) || [],
    datasets: {
      label: "Operations",
      data: analytics.operationsByDayOfWeek.map((item) => item.count) || [],
    },
  };

  // Memoize statistics card props to prevent re-renders on scroll
  const statsCardProps = useMemo(
    () => ({
      totalOperations: {
        count: analytics.totalOperations,
        percentage: {
          color: "success",
          amount: `${analytics.completedOperations}`,
          label: "completed",
        },
      },
      checkIns: {
        count: analytics.totalCheckIns,
        percentage: {
          color: "info",
          amount: `${
            analytics.totalOperations > 0
              ? Math.round((analytics.totalCheckIns / analytics.totalOperations) * 100)
              : 0
          }%`,
          label: "of total",
        },
      },
      checkOuts: {
        count: analytics.totalCheckOuts,
        percentage: {
          color: "success",
          amount: `${
            analytics.totalOperations > 0
              ? Math.round((analytics.totalCheckOuts / analytics.totalOperations) * 100)
              : 0
          }%`,
          label: "of total",
        },
      },
      activeBookings: {
        count: analytics.activeBookings,
        percentage: {
          color: "primary",
          amount: `${
            analytics.averageOperationTime > 0 ? `${analytics.averageOperationTime} min` : "N/A"
          }`,
          label: "avg. time",
        },
      },
    }),
    [analytics]
  );

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!agent) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <MDBox display="flex" alignItems="center" mb={3}>
            <IconButton onClick={() => navigate("/admin/operations-team")} sx={{ mr: 2 }}>
              <Icon>arrow_back</Icon>
            </IconButton>
            <MDTypography variant="h4" fontWeight="bold">
              Agent Not Found
            </MDTypography>
          </MDBox>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header */}
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <MDBox display="flex" alignItems="center">
            <IconButton onClick={() => navigate("/admin/operations-team")} sx={{ mr: 2 }}>
              <Icon>arrow_back</Icon>
            </IconButton>
            <Avatar
              sx={{
                bgcolor: "warning.main",
                width: 64,
                height: 64,
                fontSize: "1.5rem",
                fontWeight: "bold",
                mr: 2,
              }}
            >
              {getInitials(agent.fullName)}
            </Avatar>
            <MDBox>
              <MDTypography variant="h4" fontWeight="bold">
                {agent.fullName || "N/A"}
              </MDTypography>
              <MDBox display="flex" alignItems="center" mt={0.5}>
                <Chip
                  label={getStatusLabel(agent.isActive)}
                  color={getStatusColor(agent.isActive)}
                  size="small"
                  icon={
                    <Icon sx={{ fontSize: "1rem !important" }}>
                      {agent.isActive !== false ? "check_circle" : "cancel"}
                    </Icon>
                  }
                  sx={{ mr: 1 }}
                />
                <MDTypography variant="body2" color="text.secondary">
                  Check-In/Check-Out Agent
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
          <MDBox>
            <MDButton
              variant="outlined"
              color="warning"
              component="a"
              href={`mailto:${agent.email || ""}`}
              startIcon={<Icon>email</Icon>}
            >
              Send Email
            </MDButton>
          </MDBox>
        </MDBox>

        {/* Contact Info */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={2.5}>
            <MDTypography variant="h6" fontWeight="bold" mb={2}>
              Contact Information
            </MDTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <MDBox display="flex" alignItems="center" mb={1}>
                  <Icon sx={{ fontSize: "1.2rem", color: "text.secondary", mr: 1 }}>email</Icon>
                  <MDTypography
                    variant="body2"
                    component="a"
                    href={`mailto:${agent.email || ""}`}
                    sx={{
                      color: "primary.main !important",
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                        color: "primary.dark !important",
                      },
                    }}
                  >
                    {agent.email || "N/A"}
                  </MDTypography>
                </MDBox>
              </Grid>
              {agent.phone && (
                <Grid item xs={12} sm={6}>
                  <MDBox display="flex" alignItems="center" mb={1}>
                    <Icon sx={{ fontSize: "1.2rem", color: "text.secondary", mr: 1 }}>phone</Icon>
                    <MDTypography variant="body2" color="text.secondary">
                      {agent.phone}
                    </MDTypography>
                  </MDBox>
                </Grid>
              )}
            </Grid>
          </MDBox>
        </Card>

        {/* Statistics Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="warning"
              icon="assignment"
              title="Total Operations"
              count={statsCardProps.totalOperations.count}
              percentage={statsCardProps.totalOperations.percentage}
              sx={{ height: "100%", minHeight: "180px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="info"
              icon="login"
              title="Check-Ins"
              count={statsCardProps.checkIns.count}
              percentage={statsCardProps.checkIns.percentage}
              sx={{ height: "100%", minHeight: "180px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="success"
              icon="logout"
              title="Check-Outs"
              count={statsCardProps.checkOuts.count}
              percentage={statsCardProps.checkOuts.percentage}
              sx={{ height: "100%", minHeight: "180px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="primary"
              icon="event_available"
              title="Active Bookings"
              count={statsCardProps.activeBookings.count}
              percentage={statsCardProps.activeBookings.percentage}
              sx={{ height: "100%", minHeight: "180px" }}
            />
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
          {/* Operations Over Time */}
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="bold" mb={3}>
                  Operations Over Time
                </MDTypography>
                <DefaultLineChart
                  icon={{ color: "warning", component: "trending_up" }}
                  title="Monthly Operations"
                  description="Check-ins and check-outs handled per month"
                  height="300px"
                  chart={operationsOverTimeChart}
                />
              </MDBox>
            </Card>
          </Grid>

          {/* Operations by Type */}
          <Grid item xs={12} lg={4}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="bold" mb={3}>
                  Operations by Type
                </MDTypography>
                <DefaultDoughnutChart
                  icon={{ color: "warning", component: "pie_chart" }}
                  title="Type Distribution"
                  description="Breakdown of check-ins vs check-outs"
                  height="300px"
                  chart={operationsByTypeChart}
                />
              </MDBox>
            </Card>
          </Grid>

          {/* Peak Hours */}
          <Grid item xs={12} lg={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="bold" mb={3}>
                  Peak Activity Hours
                </MDTypography>
                <ReportsBarChart
                  color="warning"
                  title="Operations by Hour"
                  description="Most active hours of the day for operations"
                  date="24-hour distribution"
                  chart={peakHoursChart}
                />
              </MDBox>
            </Card>
          </Grid>

          {/* Day of Week Distribution */}
          <Grid item xs={12} lg={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="bold" mb={3}>
                  Operations by Day of Week
                </MDTypography>
                <ReportsBarChart
                  color="info"
                  title="Weekly Distribution"
                  description="Operations handled across different days"
                  date="7-day pattern"
                  chart={dayOfWeekChart}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Performance Metrics */}
        <Card sx={{ mt: 3 }}>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="bold" mb={3}>
              Performance Insights
            </MDTypography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <MDBox
                  sx={{
                    textAlign: "center",
                    p: 2,
                    bgcolor: "warning.lighter",
                    borderRadius: 2,
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold" color="warning.main">
                    {analytics.totalOperations}
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Total Operations
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MDBox
                  sx={{
                    textAlign: "center",
                    p: 2,
                    bgcolor: "success.lighter",
                    borderRadius: 2,
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold" color="success.main">
                    {analytics.onTimeRate > 0 ? `${analytics.onTimeRate}%` : "N/A"}
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    On-Time Rate
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MDBox
                  sx={{
                    textAlign: "center",
                    p: 2,
                    bgcolor: "info.lighter",
                    borderRadius: 2,
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold" color="info.main">
                    {analytics.averageOperationTime > 0
                      ? `${analytics.averageOperationTime} min`
                      : "N/A"}
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Avg. Operation Time
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MDBox
                  sx={{
                    textAlign: "center",
                    p: 2,
                    bgcolor: "primary.lighter",
                    borderRadius: 2,
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold" color="primary.main">
                    {analytics.customerSatisfaction > 0
                      ? `${analytics.customerSatisfaction}%`
                      : "N/A"}
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Customer Satisfaction
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>

            {/* Insights Section */}
            <Divider sx={{ my: 3 }} />
            <MDTypography variant="h6" fontWeight="bold" mb={2}>
              Key Insights
            </MDTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <MDBox
                  sx={{
                    p: 2,
                    bgcolor: "background.default",
                    borderRadius: 2,
                    borderLeft: "4px solid",
                    borderColor: "info.main",
                  }}
                >
                  <MDBox display="flex" alignItems="center" mb={1}>
                    <Icon sx={{ color: "info.main", mr: 1 }}>insights</Icon>
                    <MDTypography variant="body2" fontWeight="bold">
                      Operation Efficiency
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="body2" color="text.secondary">
                    {analytics.averageOperationTime > 0
                      ? `Average operation time is ${analytics.averageOperationTime} minutes. ${
                          analytics.averageOperationTime < 15
                            ? "Excellent performance!"
                            : analytics.averageOperationTime < 30
                            ? "Good performance."
                            : "Consider optimizing processes."
                        }`
                      : "No operation data available yet. Metrics will appear as operations are processed."}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox
                  sx={{
                    p: 2,
                    bgcolor: "background.default",
                    borderRadius: 2,
                    borderLeft: "4px solid",
                    borderColor: "success.main",
                  }}
                >
                  <MDBox display="flex" alignItems="center" mb={1}>
                    <Icon sx={{ color: "success.main", mr: 1 }}>schedule</Icon>
                    <MDTypography variant="body2" fontWeight="bold">
                      Activity Patterns
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="body2" color="text.secondary">
                    {analytics.peakHours.some((h) => h.count > 0)
                      ? "Peak activity hours identified. Consider scheduling additional support during busy periods."
                      : "Activity patterns will be analyzed as more operations are processed."}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox
                  sx={{
                    p: 2,
                    bgcolor: "background.default",
                    borderRadius: 2,
                    borderLeft: "4px solid",
                    borderColor: "warning.main",
                  }}
                >
                  <MDBox display="flex" alignItems="center" mb={1}>
                    <Icon sx={{ color: "warning.main", mr: 1 }}>trending_up</Icon>
                    <MDTypography variant="body2" fontWeight="bold">
                      Workload Distribution
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="body2" color="text.secondary">
                    {analytics.totalCheckIns > 0 || analytics.totalCheckOuts > 0
                      ? `Currently managing ${analytics.activeBookings} active bookings. ${analytics.totalCheckIns} check-ins and ${analytics.totalCheckOuts} check-outs completed.`
                      : "Workload metrics will be available as operations are assigned and completed."}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox
                  sx={{
                    p: 2,
                    bgcolor: "background.default",
                    borderRadius: 2,
                    borderLeft: "4px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <MDBox display="flex" alignItems="center" mb={1}>
                    <Icon sx={{ color: "primary.main", mr: 1 }}>star</Icon>
                    <MDTypography variant="body2" fontWeight="bold">
                      Performance Quality
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="body2" color="text.secondary">
                    {analytics.onTimeRate > 0
                      ? `On-time completion rate: ${analytics.onTimeRate}%. ${
                          analytics.onTimeRate >= 90
                            ? "Outstanding reliability!"
                            : analytics.onTimeRate >= 75
                            ? "Good performance."
                            : "Room for improvement."
                        }`
                      : "Performance metrics will be calculated as operations are tracked and completed."}
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default AgentAnalytics;
